import { useEffect, useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import {
  Users,
  Mail,
  Gift,
  Inbox,
  LogOut,
  RefreshCw,
  Search,
  Download,
  ShieldCheck,
} from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const TOKEN_KEY = "ridemate_admin_token";

export default function AdminPage() {
  const [token, setToken] = useState(() => localStorage.getItem(TOKEN_KEY) || "");
  const [password, setPassword] = useState("");
  const [loading, setLoading] = useState(false);

  if (!token) {
    return (
      <LoginScreen
        password={password}
        setPassword={setPassword}
        loading={loading}
        onSubmit={async (e) => {
          e.preventDefault();
          if (!password) return;
          setLoading(true);
          try {
            const { data } = await axios.post(`${API}/admin/login`, { password });
            if (data?.token) {
              localStorage.setItem(TOKEN_KEY, data.token);
              setToken(data.token);
              toast.success("Welcome back, Admin");
            }
          } catch (err) {
            toast.error(err?.response?.data?.detail || "Invalid password");
          } finally {
            setLoading(false);
          }
        }}
      />
    );
  }

  return (
    <Dashboard
      token={token}
      onLogout={() => {
        localStorage.removeItem(TOKEN_KEY);
        setToken("");
        toast("Signed out");
      }}
    />
  );
}

function LoginScreen({ password, setPassword, loading, onSubmit }) {
  return (
    <main
      data-testid="admin-login-page"
      className="min-h-[80vh] flex items-center justify-center pt-16 pb-24 px-6 bg-white"
    >
      <div className="w-full max-w-md rounded-3xl border border-[#e5e7eb] bg-[#F9FAFB] p-8 md:p-10">
        <div className="h-12 w-12 rounded-2xl bg-[#5FA836]/10 grid place-items-center text-[#5FA836]">
          <ShieldCheck className="w-6 h-6" />
        </div>
        <h1 className="mt-6 font-display text-3xl md:text-4xl font-black tracking-[-0.03em] text-[#24303F]">
          Admin <span className="text-gray-400">Console</span>
        </h1>
        <p className="mt-2 font-body text-gray-600 text-sm">
          Enter the admin password to view waitlist signups and messages.
        </p>
        <form onSubmit={onSubmit} className="mt-7 space-y-3">
          <input
            data-testid="admin-password-input"
            type="password"
            placeholder="Admin password"
            value={password}
            onChange={(e) => setPassword(e.target.value)}
            autoFocus
            className="w-full rounded-full bg-white border border-[#e5e7eb] px-5 py-3 font-body text-[15px] text-[#24303F] placeholder-gray-400 outline-none focus:border-[#5FA836]/60"
          />
          <button
            data-testid="admin-login-submit"
            type="submit"
            disabled={loading}
            className="btn-primary w-full rounded-full px-5 py-3 font-display disabled:opacity-60"
          >
            {loading ? "Signing in…" : "Sign in"}
          </button>
        </form>
      </div>
    </main>
  );
}

function Dashboard({ token, onLogout }) {
  const [tab, setTab] = useState("waitlist");
  const [summary, setSummary] = useState(null);
  const [waitlist, setWaitlist] = useState([]);
  const [contacts, setContacts] = useState([]);
  const [loading, setLoading] = useState(false);
  const [query, setQuery] = useState("");

  const headers = { "X-Admin-Password": token };

  const loadAll = async () => {
    setLoading(true);
    try {
      const [s, w, c] = await Promise.all([
        axios.get(`${API}/admin/summary`, { headers }),
        axios.get(`${API}/admin/waitlist`, { headers }),
        axios.get(`${API}/admin/contacts`, { headers }),
      ]);
      setSummary(s.data);
      setWaitlist(w.data.items || []);
      setContacts(c.data.items || []);
    } catch (err) {
      if (err?.response?.status === 401) {
        toast.error("Session expired");
        onLogout();
      } else {
        toast.error("Failed to load admin data");
      }
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadAll();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const downloadCSV = (rows, columns, filename) => {
    if (!rows.length) {
      toast("Nothing to export");
      return;
    }
    const head = columns.map((c) => c.label).join(",");
    const body = rows
      .map((r) =>
        columns
          .map((c) => {
            const v = c.get(r);
            const s = v == null ? "" : String(v).replace(/"/g, '""');
            return `"${s}"`;
          })
          .join(",")
      )
      .join("\n");
    const blob = new Blob([head + "\n" + body], { type: "text/csv;charset=utf-8;" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = filename;
    a.click();
    URL.revokeObjectURL(url);
  };

  const q = query.trim().toLowerCase();
  const filteredWaitlist = q
    ? waitlist.filter(
        (it) =>
          (it.email || "").toLowerCase().includes(q) ||
          (it.name || "").toLowerCase().includes(q) ||
          (it.city || "").toLowerCase().includes(q)
      )
    : waitlist;

  const filteredContacts = q
    ? contacts.filter(
        (it) =>
          (it.email || "").toLowerCase().includes(q) ||
          (it.name || "").toLowerCase().includes(q) ||
          (it.subject || "").toLowerCase().includes(q) ||
          (it.message || "").toLowerCase().includes(q)
      )
    : contacts;

  return (
    <main
      data-testid="admin-dashboard"
      className="pt-12 pb-20 min-h-screen bg-white"
    >
      <div className="mx-auto max-w-7xl px-6 lg:px-10">
        {/* Header */}
        <div className="flex flex-wrap items-end justify-between gap-4">
          <div>
            <span className="font-display text-[11px] uppercase tracking-[0.25em] text-[#5FA836] font-bold">
              Admin Console
            </span>
            <h1 className="mt-2 font-display text-4xl md:text-5xl font-black tracking-[-0.03em] leading-tight text-[#24303F]">
              Dashboard
            </h1>
          </div>
          <div className="flex gap-2">
            <button
              onClick={loadAll}
              data-testid="admin-refresh"
              className="rounded-full border border-[#e5e7eb] bg-white text-[#24303F] px-4 py-2 font-display text-sm hover:border-[#5FA836]/60 flex items-center gap-2"
            >
              <RefreshCw className={`w-4 h-4 ${loading ? "animate-spin" : ""}`} />
              Refresh
            </button>
            <button
              onClick={onLogout}
              data-testid="admin-logout"
              className="rounded-full border border-[#e5e7eb] bg-white text-[#24303F] px-4 py-2 font-display text-sm hover:border-[#5FA836]/60 hover:text-[#5FA836] flex items-center gap-2"
            >
              <LogOut className="w-4 h-4" />
              Sign out
            </button>
          </div>
        </div>

        {/* Summary cards */}
        <div className="mt-10 grid grid-cols-2 lg:grid-cols-4 gap-4">
          <StatCard
            icon={Users}
            label="Waitlist signups"
            value={summary?.waitlist_total ?? "—"}
            color="text-[#5FA836]"
          />
          <StatCard
            icon={Gift}
            label="Cashback eligible"
            value={summary?.cashback_users ?? "—"}
            sub={`of ${summary?.offer_limit ?? 100}`}
            color="text-[#24303F]"
          />
          <StatCard
            icon={Inbox}
            label="Slots remaining"
            value={summary?.slots_remaining ?? "—"}
            color="text-[#5FA836]"
          />
          <StatCard
            icon={Mail}
            label="Contact messages"
            value={summary?.contacts_total ?? "—"}
            color="text-[#24303F]"
          />
        </div>

        {/* Tabs + search */}
        <div className="mt-10 flex flex-wrap items-center gap-3 justify-between">
          <div className="inline-flex rounded-full border border-[#e5e7eb] bg-white p-1">
            <TabBtn active={tab === "waitlist"} onClick={() => setTab("waitlist")}>
              Waitlist ({waitlist.length})
            </TabBtn>
            <TabBtn active={tab === "contacts"} onClick={() => setTab("contacts")}>
              Contacts ({contacts.length})
            </TabBtn>
          </div>
          <div className="flex items-center gap-2 flex-1 max-w-md">
            <div className="relative flex-1">
              <Search className="w-4 h-4 absolute left-4 top-1/2 -translate-y-1/2 text-gray-400" />
              <input
                data-testid="admin-search"
                placeholder="Search by email, name, city…"
                value={query}
                onChange={(e) => setQuery(e.target.value)}
                className="w-full rounded-full bg-white border border-[#e5e7eb] pl-10 pr-4 py-2.5 font-body text-sm text-[#24303F] placeholder-gray-400 outline-none focus:border-[#5FA836]/60"
              />
            </div>
            <button
              data-testid="admin-export"
              onClick={() => {
                if (tab === "waitlist") {
                  downloadCSV(
                    filteredWaitlist,
                    [
                      { label: "Position", get: (r) => r.position },
                      { label: "Email", get: (r) => r.email },
                      { label: "Name", get: (r) => r.name || "" },
                      { label: "City", get: (r) => r.city || "" },
                      { label: "Role", get: (r) => r.role_interest || "" },
                      { label: "Cashback", get: (r) => (r.cashback_eligible ? "Yes" : "No") },
                      { label: "Joined", get: (r) => r.created_at },
                    ],
                    `ridemate-waitlist-${Date.now()}.csv`
                  );
                } else {
                  downloadCSV(
                    filteredContacts,
                    [
                      { label: "Name", get: (r) => r.name },
                      { label: "Email", get: (r) => r.email },
                      { label: "Subject", get: (r) => r.subject || "" },
                      { label: "Message", get: (r) => r.message },
                      { label: "Received", get: (r) => r.created_at },
                    ],
                    `ridemate-contacts-${Date.now()}.csv`
                  );
                }
              }}
              className="rounded-full border border-[#e5e7eb] bg-white text-[#24303F] px-4 py-2.5 font-display text-sm hover:border-[#5FA836]/60 flex items-center gap-2"
            >
              <Download className="w-4 h-4" />
              CSV
            </button>
          </div>
        </div>

        {/* Tables */}
        <div className="mt-6 rounded-3xl border border-[#e5e7eb] bg-[#F9FAFB] overflow-hidden">
          {tab === "waitlist" ? (
            <WaitlistTable rows={filteredWaitlist} loading={loading} />
          ) : (
            <ContactsTable rows={filteredContacts} loading={loading} />
          )}
        </div>
      </div>
    </main>
  );
}

const TabBtn = ({ active, onClick, children }) => (
  <button
    onClick={onClick}
    className={`rounded-full px-4 py-2 font-display text-sm transition-colors ${
      active ? "bg-[#5FA836] text-white" : "text-gray-600 hover:text-[#24303F]"
    }`}
  >
    {children}
  </button>
);

const StatCard = ({ icon: Icon, label, value, sub, color = "text-[#5FA836]" }) => (
  <div className="rounded-3xl border border-[#e5e7eb] bg-white p-6">
    <div className={`h-10 w-10 rounded-xl bg-[#F9FAFB] grid place-items-center ${color}`}>
      <Icon className="w-5 h-5" />
    </div>
    <div className="mt-5 font-display text-3xl md:text-4xl font-black tracking-[-0.02em] text-[#24303F]">
      {value}
      {sub && <span className="text-base font-bold text-gray-400 ml-1">{sub}</span>}
    </div>
    <div className="mt-1 font-body text-gray-500 text-xs uppercase tracking-[0.18em]">
      {label}
    </div>
  </div>
);

const formatDate = (s) => {
  if (!s) return "—";
  try {
    return new Date(s).toLocaleString("en-IN", {
      day: "2-digit",
      month: "short",
      year: "numeric",
      hour: "2-digit",
      minute: "2-digit",
    });
  } catch {
    return s;
  }
};

function WaitlistTable({ rows, loading }) {
  if (loading && !rows.length) return <Empty msg="Loading…" />;
  if (!rows.length) return <Empty msg="No waitlist signups yet." />;
  return (
    <div className="overflow-x-auto">
      <table className="w-full text-sm">
        <thead className="bg-white border-b border-[#e5e7eb]">
          <tr className="text-left font-display text-[11px] uppercase tracking-[0.18em] text-gray-500">
            <th className="px-5 py-4">#</th>
            <th className="px-5 py-4">Email</th>
            <th className="px-5 py-4">Name</th>
            <th className="px-5 py-4">City</th>
            <th className="px-5 py-4">Role</th>
            <th className="px-5 py-4">Cashback</th>
            <th className="px-5 py-4">Joined</th>
          </tr>
        </thead>
        <tbody className="font-body text-[#24303F]">
          {rows.map((r) => (
            <tr
              key={r.id || r.email}
              data-testid="waitlist-row"
              className="border-t border-[#e5e7eb] hover:bg-white"
            >
              <td className="px-5 py-4 font-display font-bold text-[#24303F]">#{r.position}</td>
              <td className="px-5 py-4">{r.email}</td>
              <td className="px-5 py-4">{r.name || "—"}</td>
              <td className="px-5 py-4">{r.city || "—"}</td>
              <td className="px-5 py-4 capitalize">{r.role_interest || "—"}</td>
              <td className="px-5 py-4">
                {r.cashback_eligible ? (
                  <span className="inline-flex items-center gap-1.5 rounded-full bg-[#5FA836]/10 border border-[#5FA836]/40 text-[#5FA836] px-2.5 py-1 text-xs font-display font-bold">
                    ₹100
                  </span>
                ) : (
                  <span className="text-gray-400">—</span>
                )}
              </td>
              <td className="px-5 py-4 text-gray-500">{formatDate(r.created_at)}</td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

function ContactsTable({ rows, loading }) {
  if (loading && !rows.length) return <Empty msg="Loading…" />;
  if (!rows.length) return <Empty msg="No contact messages yet." />;
  return (
    <div className="overflow-x-auto">
      <table className="w-full text-sm">
        <thead className="bg-white border-b border-[#e5e7eb]">
          <tr className="text-left font-display text-[11px] uppercase tracking-[0.18em] text-gray-500">
            <th className="px-5 py-4">Name</th>
            <th className="px-5 py-4">Email</th>
            <th className="px-5 py-4">Subject</th>
            <th className="px-5 py-4">Message</th>
            <th className="px-5 py-4">Received</th>
          </tr>
        </thead>
        <tbody className="font-body text-[#24303F]">
          {rows.map((r) => (
            <tr
              key={r.id || r.email + r.created_at}
              data-testid="contact-row"
              className="border-t border-[#e5e7eb] hover:bg-white align-top"
            >
              <td className="px-5 py-4 font-medium text-[#24303F]">{r.name}</td>
              <td className="px-5 py-4">{r.email}</td>
              <td className="px-5 py-4">{r.subject || "—"}</td>
              <td className="px-5 py-4 max-w-md text-gray-600">{r.message}</td>
              <td className="px-5 py-4 text-gray-500 whitespace-nowrap">{formatDate(r.created_at)}</td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

const Empty = ({ msg }) => (
  <div className="px-6 py-16 text-center font-body text-gray-500">{msg}</div>
);
